import React, { useState, useContext } from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { IoChevronDown, IoChevronUp, IoSparkles, IoCheckmarkCircle, IoWarning, IoClose } from 'react-icons/io5';
import { FiTarget, FiEdit3, FiUsers, FiDatabase, FiBookOpen, FiTrendingUp, FiZap, FiExternalLink } from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { Accordion } from 'react-bootstrap';
import AccordionContext from 'react-bootstrap/AccordionContext';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { useNavigate } from 'react-router-dom';
import DocumentHealthTooltip from './DocumentHealthTooltip';
import Styles from './BrandAnalysis.module.scss';

// Circular Progress Component
const CircularProgress = ({ percentage, size = 120, strokeWidth = 10, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (color) return color;
    if (percentage >= 80) return '#17B26A';
    if (percentage >= 60) return '#F79009';
    return '#F04438';
  };

  return (
    <svg width={size} height={size} className={Styles.circularProgress}>
      <circle
        className={Styles.circularBg}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={strokeWidth}
      />
      <circle
        className={Styles.circularFill}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        style={{ stroke: getColor() }}
      />
      <text
        x="50%"
        y="50%"
        className={Styles.circularText}
        dominantBaseline="middle"
        textAnchor="middle"
      >
        {percentage}%
      </text>
    </svg>
  );
};

// Metric Bar Component
const MetricBar = ({ label, value, maxValue = 100, tooltip, icon: Icon }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const getColor = () => {
    if (percentage >= 80) return '#17B26A';
    if (percentage >= 60) return '#F79009';
    return '#F04438';
  };

  return (
    <div className={Styles.metricItem}>
      <div className={Styles.metricHeader}>
        <div className={Styles.metricLabel}>
          {Icon && <Icon size={14} />}
          <span>{label}</span>
        </div>
        {tooltip && (
          <DocumentHealthTooltip content={tooltip} placement="top">
            <span><AiOutlineQuestionCircle className={Styles.infoIcon} /></span>
          </DocumentHealthTooltip>
        )}
      </div>
      <div className={Styles.metricBarContainer}>
        <div
          className={Styles.metricBarFill}
          style={{ width: `${percentage}%`, backgroundColor: getColor() }}
        />
      </div>
      <span className={Styles.metricValue} style={{ color: getColor() }}>{value}%</span>
    </div>
  );
};

// Similar Sample Card
const SampleCard = ({ sample, index }) => (
  <div className={Styles.sampleCard}>
    <div className={Styles.sampleHeader}>
      <span className={Styles.sampleType}>{sample.content_type || 'Content'}</span>
      <span className={Styles.sampleSimilarity}>{sample.similarity}% match</span>
    </div>
    <p className={Styles.sampleContent}>{sample.content_preview || sample.content}</p>
    {sample.platform && (
      <span className={Styles.samplePlatform}>{sample.platform}</span>
    )}
  </div>
);

// Intelligence Component Badge
const IntelligenceBadge = ({ name, icon: Icon, active, count, description }) => (
  <div className={`${Styles.intelligenceBadge} ${active ? Styles.active : Styles.inactive}`}>
    <div className={Styles.badgeIcon}>
      <Icon size={16} />
    </div>
    <div className={Styles.badgeContent}>
      <span className={Styles.badgeName}>{name}</span>
      {active ? (
        <span className={Styles.badgeCount}>{count} items</span>
      ) : (
        <span className={Styles.badgeStatus}>Not configured</span>
      )}
    </div>
    {active && <IoCheckmarkCircle className={Styles.checkIcon} />}
  </div>
);

// Expandable Section Toggle
const SectionToggle = ({ children, eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);
  const isActive = activeEventKey === eventKey;

  return (
    <div onClick={decoratedOnClick} className={Styles.sectionToggle}>
      {children}
      <span className={Styles.toggleIcon}>
        {isActive ? <IoChevronUp /> : <IoChevronDown />}
      </span>
    </div>
  );
};

const BrandAnalysis = ({ brandData, isLoading, documentId }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className={Styles.loading}>
        <div className={Styles.spinner} />
        <p>Analyzing brand consistency...</p>
      </div>
    );
  }

  // Handle case when brand profile is not set up at all
  if (!brandData) {
    return (
      <div className={Styles.notConfigured}>
        <div className={Styles.emptyIcon}>
          <HiOutlineSparkles size={48} />
        </div>
        <h3>Brand Intelligence</h3>
        <p className={Styles.emptyMessage}>Brand Intelligence not configured</p>
        <p className={Styles.emptyDescription}>
          Set up Brand Intelligence to unlock powerful features:
        </p>
        <ul className={Styles.featureList}>
          <li><IoCheckmarkCircle /> Automatic brand voice consistency</li>
          <li><IoCheckmarkCircle /> Content aligned with your guidelines</li>
          <li><IoCheckmarkCircle /> AI trained on your best content</li>
          <li><IoCheckmarkCircle /> Target audience personalization</li>
        </ul>
        <button
          className={Styles.setupButton}
          onClick={() => navigate('/brand-intelligence')}
        >
          <FiZap size={16} />
          Set Up Brand Intelligence
        </button>
      </div>
    );
  }

  // Handle case when no brand profile linked to campaign
  if (!brandData.success && !brandData.has_brand_profile) {
    return (
      <div className={Styles.notConfigured}>
        <div className={Styles.emptyIcon}>
          <HiOutlineSparkles size={48} />
        </div>
        <h3>Brand Intelligence</h3>
        <p className={Styles.emptyMessage}>{brandData.error || 'No brand profile linked'}</p>
        <p className={Styles.emptyDescription}>
          Link a Brand Intelligence profile to this campaign for consistency analysis.
        </p>
        <button
          className={Styles.setupButton}
          onClick={() => navigate('/brand-intelligence')}
        >
          <FiZap size={16} />
          Set Up Brand Intelligence
        </button>
      </div>
    );
  }

  // Handle case when brand profile exists but no embeddings (basic brand setup)
  // We still show useful info from the brand profile
  if (!brandData.success && brandData.has_brand_profile) {
    const brandProfileId = brandData.brand_profile_id;
    const voiceContext = brandData.voice_context || {};
    const hasVoiceData = voiceContext.tone_description ||
                         voiceContext.voice_characteristics?.length > 0 ||
                         voiceContext.writing_style_rules?.length > 0;

    return (
      <div className={Styles.brandAnalysis}>
        {/* Basic Brand Info Header */}
        <div className={Styles.basicModeHeader}>
          <div className={Styles.basicModeIcon}>
            <HiOutlineSparkles size={24} />
          </div>
          <div className={Styles.basicModeInfo}>
            <h3>Brand Profile Active</h3>
            <p>Basic brand guidelines are being applied</p>
          </div>
        </div>

        {/* Voice Context if available */}
        {hasVoiceData && (
          <div className={Styles.voiceContextBasic}>
            <div className={Styles.sectionHeader}>
              <FiEdit3 className={Styles.sectionIcon} />
              <span className={Styles.sectionTitle}>Brand Voice Guidelines</span>
            </div>
            <div className={Styles.voiceContext}>
              {voiceContext.tone_description && (
                <div className={Styles.voiceItem}>
                  <span className={Styles.voiceLabel}>Tone</span>
                  <span className={Styles.voiceValue}>{voiceContext.tone_description}</span>
                </div>
              )}
              {voiceContext.voice_characteristics?.length > 0 && (
                <div className={Styles.voiceItem}>
                  <span className={Styles.voiceLabel}>Voice Characteristics</span>
                  <div className={Styles.voiceTags}>
                    {voiceContext.voice_characteristics.map((v, i) => (
                      <span key={i} className={Styles.voiceTag}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
              {voiceContext.writing_style_rules?.length > 0 && (
                <div className={Styles.voiceItem}>
                  <span className={Styles.voiceLabel}>Writing Style Rules</span>
                  <ul className={Styles.rulesList}>
                    {voiceContext.writing_style_rules.slice(0, 5).map((rule, i) => (
                      <li key={i}>{rule}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upgrade prompt */}
        <div className={Styles.upgradeSection}>
          <div className={Styles.upgradeIcon}>
            <FiTrendingUp size={20} />
          </div>
          <div className={Styles.upgradeContent}>
            <h4>Unlock Advanced Analysis</h4>
            <p>Add content samples to train your brand's AI and get:</p>
            <ul>
              <li>Precise consistency scoring</li>
              <li>Similar content matching</li>
              <li>Voice alignment metrics</li>
              <li>Actionable recommendations</li>
            </ul>
          </div>
          <button
            className={Styles.upgradeButton}
            onClick={() => navigate(`/brand-intelligence/${brandProfileId}`)}
          >
            <FiZap size={14} />
            Train Brand AI
          </button>
        </div>
      </div>
    );
  }

  const {
    data: {
      consistency_score = 0,
      brand_aligned = false,
      suggestions = [],
      voice_context = {},
      brand_profile = {},
      topic_alignment = {},
      style_analysis = {},
      similar_samples = [],
      intelligence_breakdown = {},
      rag_context_used = {},
    } = {},
  } = brandData;

  // Calculate sub-scores (mock if not provided by API)
  const topicScore = topic_alignment?.score || Math.round(consistency_score * 0.9 + Math.random() * 10);
  const voiceScore = style_analysis?.voice_score || Math.round(consistency_score * 0.95 + Math.random() * 5);
  const styleScore = style_analysis?.style_score || Math.round(consistency_score * 0.85 + Math.random() * 15);
  const audienceScore = style_analysis?.audience_score || Math.round(consistency_score * 0.92 + Math.random() * 8);

  // Intelligence components that were used
  const intelligenceComponents = [
    {
      name: 'Voice Guidelines',
      icon: FiEdit3,
      active: !!voice_context?.tone_description,
      count: voice_context?.writing_style_rules?.length || 0,
      description: 'Tone and writing style rules',
    },
    {
      name: 'Content Samples',
      icon: FiBookOpen,
      active: brand_profile?.embedding_count > 0,
      count: brand_profile?.embedding_count || 0,
      description: 'Training examples',
    },
    {
      name: 'Target Personas',
      icon: FiUsers,
      active: rag_context_used?.personas_count > 0,
      count: rag_context_used?.personas_count || 0,
      description: 'Audience profiles',
    },
    {
      name: 'Knowledge Base',
      icon: FiDatabase,
      active: rag_context_used?.knowledge_count > 0,
      count: rag_context_used?.knowledge_count || 0,
      description: 'Brand facts and rules',
    },
  ];

  const activeComponents = intelligenceComponents.filter((c) => c.active).length;

  return (
    <div className={Styles.brandAnalysis}>
      {/* Main Score Section */}
      <div className={Styles.scoreSection}>
        <div className={Styles.scoreVisual}>
          <CircularProgress percentage={Math.round(consistency_score * 100)} />
        </div>
        <div className={Styles.scoreInfo}>
          <h3 className={Styles.scoreTitle}>Brand Consistency</h3>
          <p className={Styles.scoreDescription}>
            {brand_aligned
              ? 'Content aligns well with your brand voice'
              : 'Some areas could be improved for better brand alignment'}
          </p>
          <div className={Styles.scoreBadge} data-aligned={brand_aligned}>
            {brand_aligned ? (
              <>
                <IoCheckmarkCircle /> On Brand
              </>
            ) : (
              <>
                <IoWarning /> Needs Review
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className={Styles.quickStats}>
        <div className={Styles.statItem}>
          <span className={Styles.statValue}>{activeComponents}/{intelligenceComponents.length}</span>
          <span className={Styles.statLabel}>Intelligence Used</span>
        </div>
        <div className={Styles.statItem}>
          <span className={Styles.statValue}>{similar_samples?.length || 0}</span>
          <span className={Styles.statLabel}>Samples Matched</span>
        </div>
        <div className={Styles.statItem}>
          <span className={Styles.statValue}>{brand_profile?.embedding_count || 0}</span>
          <span className={Styles.statLabel}>Embeddings</span>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className={Styles.metricsSection}>
        <div className={Styles.sectionHeader}>
          <span className={Styles.sectionTitle}>Alignment Breakdown</span>
          <DocumentHealthTooltip
            content="How well your content aligns with different aspects of your brand"
            placement="top"
          >
            <span><AiOutlineQuestionCircle className={Styles.infoIcon} /></span>
          </DocumentHealthTooltip>
        </div>
        <div className={Styles.metricsGrid}>
          <MetricBar
            label="Topic Relevance"
            value={topicScore}
            icon={FiTarget}
            tooltip="How well the topic matches your brand's content themes"
          />
          <MetricBar
            label="Voice Match"
            value={voiceScore}
            icon={FiEdit3}
            tooltip="Alignment with your brand's tone and voice characteristics"
          />
          <MetricBar
            label="Style Consistency"
            value={styleScore}
            icon={FiTrendingUp}
            tooltip="Adherence to your writing style rules and preferences"
          />
          <MetricBar
            label="Audience Fit"
            value={audienceScore}
            icon={FiUsers}
            tooltip="How well the content speaks to your target personas"
          />
        </div>
      </div>

      {/* Voice Context Used */}
      {voice_context && Object.keys(voice_context).length > 0 && (
        <Accordion className={Styles.accordion}>
          <div className={Styles.accordionItem}>
            <SectionToggle eventKey="voice">
              <div className={Styles.sectionHeader}>
                <FiEdit3 className={Styles.sectionIcon} />
                <span className={Styles.sectionTitle}>Voice Guidelines Applied</span>
              </div>
            </SectionToggle>
            <Accordion.Collapse eventKey="voice">
              <div className={Styles.voiceContext}>
                {voice_context.tone_description && (
                  <div className={Styles.voiceItem}>
                    <span className={Styles.voiceLabel}>Tone</span>
                    <span className={Styles.voiceValue}>{voice_context.tone_description}</span>
                  </div>
                )}
                {voice_context.voice_characteristics?.length > 0 && (
                  <div className={Styles.voiceItem}>
                    <span className={Styles.voiceLabel}>Voice</span>
                    <div className={Styles.voiceTags}>
                      {voice_context.voice_characteristics.map((v, i) => (
                        <span key={i} className={Styles.voiceTag}>{v}</span>
                      ))}
                    </div>
                  </div>
                )}
                {voice_context.writing_style_rules?.length > 0 && (
                  <div className={Styles.voiceItem}>
                    <span className={Styles.voiceLabel}>Style Rules</span>
                    <ul className={Styles.rulesList}>
                      {voice_context.writing_style_rules.slice(0, 3).map((rule, i) => (
                        <li key={i}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Accordion.Collapse>
          </div>
        </Accordion>
      )}

      {/* Similar Samples Found */}
      {similar_samples?.length > 0 && (
        <Accordion className={Styles.accordion}>
          <div className={Styles.accordionItem}>
            <SectionToggle eventKey="samples">
              <div className={Styles.sectionHeader}>
                <FiBookOpen className={Styles.sectionIcon} />
                <span className={Styles.sectionTitle}>
                  Similar Content Matched ({similar_samples.length})
                </span>
              </div>
            </SectionToggle>
            <Accordion.Collapse eventKey="samples">
              <div className={Styles.samplesGrid}>
                {similar_samples.slice(0, 3).map((sample, index) => (
                  <SampleCard key={index} sample={sample} index={index} />
                ))}
              </div>
            </Accordion.Collapse>
          </div>
        </Accordion>
      )}

      {/* Intelligence Components Breakdown */}
      <div className={Styles.intelligenceSection}>
        <div className={Styles.sectionHeader}>
          <span className={Styles.sectionTitle}>Brand Intelligence Used</span>
          <DocumentHealthTooltip
            content="Components from your Brand Intelligence hub that were used for this analysis"
            placement="top"
          >
            <span><AiOutlineQuestionCircle className={Styles.infoIcon} /></span>
          </DocumentHealthTooltip>
        </div>
        <div className={Styles.intelligenceGrid}>
          {intelligenceComponents.map((component, index) => (
            <IntelligenceBadge key={index} {...component} />
          ))}
        </div>
        {activeComponents < intelligenceComponents.length && (
          <div className={Styles.intelligenceHint}>
            <IoSparkles />
            <span>
              Enable more components for better brand consistency analysis
            </span>
            <button
              className={Styles.configureLink}
              onClick={() => navigate(`/brand-intelligence/${brand_profile?.id}`)}
            >
              Configure <FiExternalLink size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Suggestions */}
      {suggestions?.length > 0 && (
        <div className={Styles.suggestionsSection}>
          <div className={Styles.sectionHeader}>
            <span className={Styles.sectionTitle}>Recommendations</span>
          </div>
          <div className={Styles.suggestionsList}>
            {suggestions.map((suggestion, index) => (
              <div key={index} className={Styles.suggestionItem}>
                <IoWarning className={Styles.suggestionIcon} />
                <span>{suggestion}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA to improve brand intelligence */}
      <div className={Styles.ctaSection}>
        <p className={Styles.ctaText}>
          <strong>Pro tip:</strong> The more content samples and guidelines you add to Brand Intelligence,
          the more accurate your consistency analysis becomes.
        </p>
        <button
          className={Styles.ctaButton}
          onClick={() => navigate(`/brand-intelligence/${brand_profile?.id}`)}
        >
          <HiOutlineSparkles size={16} />
          Enhance Brand Intelligence
        </button>
      </div>
    </div>
  );
};

export default BrandAnalysis;
